import { Box, createListCollection } from '@chakra-ui/react';
import { SearchBar } from '@himarley/unity';
import debounce from 'lodash/debounce';
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';

import Spinner from '@app/components/chakra/spinner';
import UserRow from '@app/components/chakra/user-row';
import { useOperatorsQuery } from '@app/services/operator';
import { type User } from '@app/types/api/user';
import { type StateType } from '@app/types/reducer-state';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@chakra-snippets/select';

const getDropdownLabelItems = (auth: {
  user: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    title?: string;
    imageUrl?: string;
    _id?: string;
  };
}) => {
  const {
    firstName,
    lastName,
    email: authEmail,
    role: authRole,
    title: authTitle,
    imageUrl,
    _id: authId,
  } = auth.user;

  const me = firstName && lastName ? `${firstName} ${lastName} (me)` : 'Me';

  return [
    {
      id: authId,
      label: me,
      email: authEmail,
      role: authRole,
      title: authTitle,
      imageUrl,
    },
    { id: 'unassigned', label: 'Unassigned' },
  ];
};

interface AssignUserProps {
  id: string;
  rowId: string;
  selectedId: string;
  handleSelect: (id: string, rowId: string) => void;
  contentRef?: React.RefObject<HTMLDivElement>;
  defaultLabel?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  matchWidth?: boolean;
  selectedOperator?: User;
  shouldPrefetchSelectedUser?: boolean;
  isLoadingPreSelectedOperators?: boolean;
}

const AssignUser: React.FC<AssignUserProps> = ({
  id,
  rowId,
  selectedId,
  handleSelect,
  contentRef,
  defaultLabel,
  size = 'sm',
  matchWidth = false,
  selectedOperator = {},
  shouldPrefetchSelectedUser = false,
  isLoadingPreSelectedOperators = false,
}) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const observer = useRef<IntersectionObserver | null>(null);

  const selectedOperatorQuery = useOperatorsQuery({
    ids: shouldPrefetchSelectedUser && selectedId ? [selectedId] : [],
  });

  const { data: selectedOperatorData, isLoading: isLoadingSelectedUser } = selectedOperatorQuery;

  const preSelectedOperator = useMemo(() => {
    if (!shouldPrefetchSelectedUser) {
      return null;
    }
    return selectedOperatorData?.pages[0]?.operators?.[0] || {};
  }, [selectedOperatorData, shouldPrefetchSelectedUser]);

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300),
    [],
  );

  useEffect(() => {
    if (searchFilter.length > 2 || searchFilter.length === 0) {
      debouncedSetSearch(searchFilter);
    }
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchFilter, debouncedSetSearch]);

  const operatorsQuery = useOperatorsQuery({
    searchText: debouncedSearch || '',
  });

  const {
    data: allOperators,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingOperators,
  } = operatorsQuery;

  const lastPostElementRef = useCallback(
    (node: Element | null) => {
      if (!hasNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      if (node) observer.current?.observe(node);
    },
    [fetchNextPage, hasNextPage],
  );

  const users = useMemo(() => {
    const allOps = allOperators?.pages.reduce((accumulator: User[], item) => {
      const operators = item.operators || [];
      return [...accumulator, ...operators];
    }, []) || [];

    let preSelectedOp = selectedOperator;

    if (shouldPrefetchSelectedUser) {
      preSelectedOp = preSelectedOperator;
    }

    const mergedOperators = preSelectedOp?.id
      ? [preSelectedOp, ...allOps]
      : allOps;
    return [...new Map(mergedOperators.map((op) => [op?.id, op])).values()];
  }, [
    allOperators?.pages,
    selectedOperator,
    preSelectedOperator,
    shouldPrefetchSelectedUser,
  ]);

  const { auth, recentlyAssignedOperators } = useSelector(
    (state: StateType) => ({
      auth: state.auth,
      recentlyAssignedOperators: [
        ...(state.auth?.user ? [state.auth.user] : []),
        ...(state.profile?.properties?.recentlyAssignedOperators?.[id] || []),
      ]
        .map((operator: User) => ({
          id: operator.id || operator._id,
          label: operator.name || `${operator.firstName} ${operator.lastName}`,
          email: operator.email,
          title: operator.title,
          role: operator.role,
          imageUrl: operator.imageUrl,
        }))
        .filter(
          (operator, index, self) => index === self.findIndex((o) => o.id === operator.id),
        ),
    }),
  );

  const operatorList = useMemo(
    () => users.map((user: User) => (user.id === auth.user._id
      ? {
        ...user,
        label: `${auth.user.firstName} ${auth.user.lastName} (me)`,
      }
      : user)),
    [users, auth.user._id, auth.user.firstName, auth.user.lastName],
  );

  const dropdownLabelItems = useMemo(() => getDropdownLabelItems(auth), [auth]);

  const renderMenuItems = useCallback(
    (list: User[]) => list
      .filter((option) => option.id !== auth.user._id && option?.id)
      .map((option) => (
        <SelectItem
          data-dropdown-item={`item-${option.id}`}
          key={option.id}
          onClick={() => handleSelect(option.id || '', rowId)}
          item={option}
          data-testid={`item-${option?.id}`}
          title={option?.email}
        >
          <UserRow option={option} size="sm" />
        </SelectItem>
      )),
    [auth.user._id, handleSelect, rowId],
  );

  const userOptions = dropdownLabelItems.map((item) => ({
    id: item.id,
    label: item.label,
  }));

  if (searchFilter === '') {
    userOptions.push(
      ...recentlyAssignedOperators.map((item) => ({
        id: item.id,
        label: item.label,
      })),
    );
  }

  userOptions.push(
    ...operatorList.map((item: User) => ({
      id: item.id,
      label: item.name || '',
    })),
  );

  const shouldShowSpinner = (!selectedOperator?.name || !preSelectedOperator?.name)
    && (isLoadingSelectedUser || isLoadingPreSelectedOperators);

  const recentlyAssignedOperatorsDeduped = useMemo(
    () => {
      const ids = new Set(operatorList.map((item) => item?.id));
      return recentlyAssignedOperators.filter((item) => !ids.has(item.id));
    },
    [recentlyAssignedOperators, operatorList],
  );

  return (
    <SelectRoot
      data-testid="assign-user-action-menu"
      collection={createListCollection({
        items: userOptions,
        itemToString: (item) => item.label || '',
        itemToValue: (item) => item.id || '',
      })}
      value={selectedId ? [selectedId] : undefined}
      size={size}
      positioning={matchWidth ? { sameWidth: true } : undefined}
    >
      <SelectTrigger>
        {shouldShowSpinner ? (
          <Spinner data-testid="selected-user-spinner" />
        ) : (
          <SelectValueText
            placeholder={
              selectedOperator?.name
              || preSelectedOperator?.name
              || defaultLabel
            }
          />
        )}
      </SelectTrigger>
      <SelectContent
        data-testid="assign-user-action-menu-list"
        portalRef={contentRef}
        w={!matchWidth ? '250px' : undefined}
      >
        <Box
          pb={1}
          px={3}
          w={matchWidth ? '100%' : 'inherit'}
          bg="white"
          pos="fixed"
          zIndex={1}
          paddingTop="5px"
          top={0}
          css={{ '& div': { width: 'unset' } }}
        >
          <SearchBar
            id="operators"
            placeholder="Search Operators"
            value={searchFilter}
            onValueChange={setSearchFilter}
            onClear={() => setSearchFilter('')}
          />
        </Box>
        <Box marginTop="40px">
          {dropdownLabelItems.map((option) => (
            <SelectItem
              data-dropdown-item={`item-${option.id}`}
              key={option.id}
              onClick={() => handleSelect(option.id || '', rowId)}
              item={option}
            >
              <UserRow option={option} size="sm" />
            </SelectItem>
          ))}
        </Box>
        {searchFilter === '' && renderMenuItems(recentlyAssignedOperatorsDeduped)}
        {renderMenuItems(operatorList)}
        <Box>
          {isFetchingNextPage ? <Spinner /> : <div ref={lastPostElementRef} />}
          {isLoadingOperators && <Spinner />}
        </Box>
      </SelectContent>
    </SelectRoot>
  );
};

export default AssignUser;
